import { AppIcon } from '@backrs_ui/common-ui';

import { Box, MenuItem, Select, SxProps, Theme, Typography } from '@mui/material';

export type CategoryTab = Readonly<{
  value: string;
  label: string;
}>;

type CoachesCategoryFilterProps = Readonly<{
  categories: CategoryTab[];
  selectedCategory: string;
  onCategoryChange: (category: string) => void;
}>;

export const CoachesCategoryFilter = ({
  categories,
  selectedCategory,
  onCategoryChange,
}: CoachesCategoryFilterProps) => {
  const isFiltered = selectedCategory !== 'all';

  return (
    <Select
      id="coaches-category-filter"
      value={selectedCategory}
      onChange={(e) => onCategoryChange(e.target.value)}
      displayEmpty
      renderValue={() => {
        const selected = categories.find((c) => c.value === selectedCategory);

        return (
          <Box sx={renderValueSx}>
            <AppIcon
              iconName="FilterLines"
              diameter="20px"
              color={isFiltered ? 'Colors/Text/text-primary' : 'Colors/Text/text-placeholder'}
            />
            <Typography
              variant="text-md-medium"
              color={({ backrs }) =>
                isFiltered
                  ? backrs.colors['Colors/Text/text-primary']
                  : backrs.colors['Colors/Text/text-placeholder']
              }
            >
              {selected?.label ?? 'Category'}
            </Typography>
            <AppIcon
              iconName="ChevronDown"
              diameter="20px"
              color={isFiltered ? 'Colors/Text/text-primary' : 'Colors/Text/text-placeholder'}
            />
          </Box>
        );
      }}
      sx={selectSx(isFiltered)}
      MenuProps={{
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        PaperProps: { sx: menuPaperSx },
      }}
    >
      {categories.map((category) => {
        const isSelected = category.value === selectedCategory;

        return (
          <MenuItem
            key={category.value}
            value={category.value}
            id={`coaches-category-filter-option-${category.value}`}
          >
            <Box sx={menuItemContentSx}>
              <Box sx={checkIconSx(isSelected)}>
                <AppIcon
                  iconName={isSelected ? 'Check' : 'Square'}
                  diameter="16px"
                  color={isSelected ? 'Colors/Foreground/fg-white' : 'currentColor'}
                />
              </Box>
              <Typography variant="text-md-medium">{category.label}</Typography>
            </Box>
          </MenuItem>
        );
      })}
    </Select>
  );
};

const renderValueSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  marginRight: backrs.spacing['spacing-md'],
});

const selectSx =
  (isFiltered: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    borderRadius: backrs.radius['radius-md'],
    border: `1px solid ${isFiltered ? backrs.colors['Colors/Foreground/fg-primary'] : backrs.colors['Colors/Foreground/fg-quaternary']}`,
    boxShadow: backrs.shadows['shadow-xs'],
    backgroundColor: backrs.colors['Colors/Background/bg-primary'],
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& .MuiSelect-icon': { display: 'none' },
    '&:hover': {
      border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
      boxShadow: backrs.shadows['shadow-lg'],
    },
  });

const menuPaperSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-md'],
  boxShadow: backrs.shadows['shadow-xl'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
  borderRadius: backrs.radius['radius-md'],
});

const menuItemContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
  width: '100%',
});

const checkIconSx =
  (isChecked: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '16px',
    height: '16px',
    backgroundColor: isChecked ? backrs.colors['Colors/Background/bg-brand-solid'] : 'transparent',
    borderRadius: backrs.radius['radius-xs'],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: isChecked ? `1px solid ${backrs.colors['Colors/Base/Black']}` : 'none',
  });
